import React, { useState } from "react";
import { Link } from "react-router-dom";

import { Logo } from "../../../shared/components/Logo";
import { signInWithCredentials, CognitoAuthResponse } from "../client";

interface SignInPageProps {
  onAuthComplete: () => void;
  onNewPasswordRequired: (session: string, username: string) => void;
}

export function SignInPage({ onAuthComplete, onNewPasswordRequired }: SignInPageProps): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setSubmitting(true);
    try {
      const result: CognitoAuthResponse = await signInWithCredentials(email.trim(), password);

      if (result.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        const username = result.ChallengeParameters?.USER_ID_FOR_SRP ?? email.trim();
        onNewPasswordRequired(result.Session ?? "", username);
        return;
      }

      if (result.AuthenticationResult) {
        onAuthComplete();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign-in failed. Please try again.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-page auth-page-signin" aria-labelledby="signin-title">
      <section className="auth-card">
        <div className="auth-logo-area">
          <Logo size={40} />
          <span className="auth-logo-text">Q<span className="auth-logo-accent">picks</span></span>
        </div>

        <h1 id="signin-title" className="auth-title">Sign in</h1>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label htmlFor="signin-email" className="auth-label">Email</label>
          <input
            id="signin-email"
            className="auth-input"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />

          <label htmlFor="signin-password" className="auth-label">Password</label>
          <input
            id="signin-password"
            className="auth-input"
            type="password"
            placeholder="••••••••"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />

          <button type="submit" className="auth-primary-button" disabled={submitting}>
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <Link to="/forgot-password" className="auth-link">Forgot your password?</Link>

        {error && (
          <p role="alert" className="auth-error">{error}</p>
        )}

        <p className="auth-note">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </section>
    </main>
  );
}
